import type { ListSkeletonPropTypes } from './List.types';
import type { AvatarProps } from '@rango-dev/whale';
import type { ReactElement } from 'react';

import { Box, Flex, Separator, Skeleton } from '@rango-dev/whale';
import React, { Fragment } from 'react';

import styles from './List.module.css';

/*
 * Mirrors the Radix Themes Avatar `size` scale (size N → --space token) so the
 * skeleton box matches the real Avatar it stands in for.
 */
const AVATAR_SIZE_PX: Record<string, number> = {
  '1': 24,
  '2': 32,
  '3': 40,
  '4': 48,
  '5': 64,
  '6': 80,
  '7': 96,
  '8': 128,
  '9': 160,
};
const DEFAULT_AVATAR_SIZE_PX = 40;
const TOP_USER_RANK_THRESHOLD = 3;

function sizeTokenToPx(size: unknown): string {
  const px = typeof size === 'string' ? AVATAR_SIZE_PX[size] : undefined;
  return `${px ?? DEFAULT_AVATAR_SIZE_PX}px`;
}

/*
 * Accepts a plain size ('4') or a responsive object ({ initial: '3', md: '4' })
 * and returns the matching px value(s) for the skeleton's width/height.
 */
function resolveAvatarSize(size: AvatarProps['size']): string | Record<string, string> {
  if (size && typeof size === 'object') {
    return Object.fromEntries(
      Object.entries(size).map(([breakpoint, value]) => [breakpoint, sizeTokenToPx(value)]),
    );
  }
  return sizeTokenToPx(size);
}

export function ListSkeleton(props: ListSkeletonPropTypes): ReactElement {
  const { maxLength, showLevel = false, avatarSize, enableTopUserSkeleton = false } = props;
  const avatarSkeletonSize = resolveAvatarSize(avatarSize);

  return (
    <Flex direction="column">
      {Array.from({ length: maxLength }, (_, index) => index).map((index) => {
        const showTopUserStyles = index < TOP_USER_RANK_THRESHOLD && enableTopUserSkeleton;
        const isLast = index === maxLength - 1;
        return (
          <Fragment key={`leaderboard-skeleton-${index}`}>
            <Box className={styles.item} p="2">
              <Flex
                className="user-item-row"
                justify="between"
                align="center"
                gap="3"
                height={'48px'}>
                <Flex align="center">
                  {showTopUserStyles ? (
                    <Box className={`${styles.topRanks} ${styles[`topRank${index + 1}`]}`}>
                      <img
                        className={styles.topRankImage}
                        src={`/imgs/leaderboard/rank${index + 1}.svg`}
                        alt=""
                      />
                      <Box className={styles.topRankBorder} />
                      <Box className={styles.topRankGradient} />
                    </Box>
                  ) : (
                    <Skeleton width="48px" height="20px" mr={{ initial: '2', sm: '4' }} />
                  )}
                  <Box position="relative" mr={{ initial: '2', sm: '4' }}>
                    <Skeleton width={avatarSkeletonSize} height={avatarSkeletonSize} />
                    {showLevel && (
                      <Skeleton width="24px" height="18px" className={styles.levelBadge} />
                    )}
                  </Box>
                  <Skeleton
                    mr={{ initial: '2', sm: '4' }}
                    width={{ initial: '96px', md: '267px' }}
                    height={{ initial: '20px', md: '24px' }}
                  />
                </Flex>
                <Flex gap="1">
                  <Box display={{ initial: 'none', md: 'contents' }}>
                    <Skeleton width="24px" height="24px" />
                  </Box>
                  <Skeleton
                    width={{ initial: '70px', md: '53px' }}
                    height={{ initial: '20px', md: '24px' }}
                  />
                </Flex>
              </Flex>
            </Box>
            {!isLast && (
              <Flex py="1">
                <Separator size="4" />
              </Flex>
            )}
          </Fragment>
        );
      })}
    </Flex>
  );
}
